import { Slider } from "@heroui/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";

type SliderStepProps = {
  question: string;
  description?: string;
  min?: number;
  max?: number;
  step?: number;
  initialValue?: number;
  /** Optional unit suffix shown next to the value (e.g., " years", " kg"). */
  unit?: string;
  /** Optional pre-formatted value override (e.g., custom labels). */
  formatValue?: (value: number) => string;
  onChange?: (value: number) => void;
  className?: string;
  testId?: string;
};

const SliderStepImpl = ({
  question,
  description,
  min = 0,
  max = 100,
  step = 1,
  initialValue,
  unit = "",
  formatValue,
  onChange,
  className,
  testId,
}: SliderStepProps) => {
  const [value, setValue] = useState<number>(initialValue ?? Math.floor((min + max) / 2));

  const handleChange = (next: number | number[]): void => {
    const v = Array.isArray(next) ? (next[0] ?? min) : next;
    setValue(v);
    onChange?.(v);
  };

  const display = formatValue ? formatValue(value) : `${value}${unit}`;

  return (
    <section data-testid={testId} className={`flex flex-col gap-6 ${className ?? ""}`}>
      <StepHeading title={question} description={description} editablePath="question" />
      <div className="flex flex-col items-center gap-4">
        <div
          className="text-4xl font-bold tabular-nums"
          style={{ color: "hsl(var(--brand-bg-foreground))" }}
        >
          {display}
        </div>
        <Slider
          aria-label={question}
          minValue={min}
          maxValue={max}
          step={step}
          value={value}
          onChange={handleChange}
          className="w-full"
        >
          <Slider.Track>
            <Slider.Fill />
            <Slider.Thumb />
          </Slider.Track>
        </Slider>
        <div
          className="flex w-full justify-between text-xs"
          style={{ color: "hsl(var(--hero-fg-muted))" }}
        >
          <span>
            {min}
            {unit}
          </span>
          <span>
            {max}
            {unit}
          </span>
        </div>
      </div>
    </section>
  );
};

export const SliderStep = memo(SliderStepImpl);

export default SliderStep;
